import { useId, useRef, type ReactNode } from "react";
import { useFocusTrap } from "../lib/focus-trap.ts";
import { Button } from "./Button.tsx";

/**
 * A short question before an action that other people feel: move a session, archive a group.
 *
 * Centred over the page at every width, with the safe choice first in the tab order and the action
 * second. Escape and a click outside mean "no". The same trap as `Panel`, so focus goes in on open
 * and back to the thing that opened it on close.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = "Keep it",
  tone = "primary",
  loading = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` for an action that takes something away. */
  tone?: "primary" | "danger";
  loading?: boolean;
  /** The confirm waits for something (a preview); Cancel stays open. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, open, loading ? undefined : onCancel);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={loading ? undefined : onCancel}
        className="absolute inset-0 bg-ink/35 backdrop-blur-[2px]"
      />
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full max-w-[440px] rounded-card border border-line bg-paper p-5 text-ink shadow-[0_24px_60px_-24px_rgba(16,25,23,0.5)] outline-none"
      >
        <h2 id={titleId} className="text-[19px]">
          {title}
        </h2>
        {children ? <div className="mt-2 text-[14px] text-ink-2">{children}</div> : null}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} loading={loading} disabled={confirmDisabled} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
